import { Link, NavLink, useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { resolveImageUrl } from "../lib/imageUrl";
import { ShoppingCart, Menu, X, Languages } from "lucide-react";
import { useEffect, useState } from "react";
import api from "../lib/api";

export default function Navbar() {
  const { t, lang, toggleLang, cartCount, admin } = useApp();
  const [open, setOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    api
      .get("/gallery")
      .then((r) => {
        const logos = r.data?.logo || [];
        if (logos.length > 0) setLogoUrl(logos[0].image_url);
      })
      .catch(() => {});
  }, []);

  const linkClass = ({ isActive }) =>
    `text-sm tracking-wide hover:text-[#274e13] transition-colors ${
      isActive ? "text-[#274e13] font-semibold" : "text-stone-700"
    }`;

  return (
    <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-stone-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-2">
          {logoUrl ? (
            <img
              src={resolveImageUrl(logoUrl)}
              alt="SVRM logo"
              data-testid="brand-logo"
              className="h-12 w-auto max-w-[160px] object-contain"
            />
          ) : (
            <div className="w-9 h-9 rounded-sm bg-[#274e13] grid place-items-center text-[#e69138] font-bold">
              ఎస్
            </div>
          )}
          <div className="leading-tight">
            <div className="text-[11px] tracking-[0.18em] text-stone-500 uppercase">SVRM</div>
            <div className="text-sm font-semibold text-stone-900">
              {lang === "en" ? "Sri Venkateswara Rice Mill" : "శ్రీ వెంకటేశ్వర రైస్ మిల్"}
            </div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <NavLink data-testid="nav-home" to="/" className={linkClass} end>
            {t.nav.home}
          </NavLink>
          <NavLink data-testid="nav-products" to="/products" className={linkClass}>
            {t.nav.products}
          </NavLink>
          <NavLink data-testid="nav-contact" to="/contact" className={linkClass}>
            {t.nav.contact}
          </NavLink>
          <NavLink data-testid="nav-wholesale" to="/customer/login" className={linkClass}>
            {lang === "en" ? "Wholesale" : "హోల్‌సేల్"}
          </NavLink>
        </nav>

        <div className="flex items-center gap-2">
          <button
            data-testid="lang-toggle"
            onClick={toggleLang}
            className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-stone-300 text-xs font-semibold tracking-wide hover:bg-stone-100 transition-colors"
          >
            <Languages size={14} />
            {lang === "en" ? "తెలుగు" : "English"}
          </button>

          <button
            data-testid="cart-button"
            onClick={() => navigate("/cart")}
            className="relative p-2 rounded-sm hover:bg-stone-100 transition-colors"
            aria-label="cart"
          >
            <ShoppingCart size={20} className="text-stone-700" />
            {cartCount > 0 && (
              <span
                data-testid="cart-count"
                className="absolute -top-1 -right-1 bg-[#b45f06] text-white text-[10px] font-bold rounded-full w-5 h-5 grid place-items-center"
              >
                {cartCount}
              </span>
            )}
          </button>

          {admin ? (
            <Link
              data-testid="nav-admin-dashboard"
              to="/admin"
              className="hidden sm:inline-flex text-xs font-semibold px-3 py-1.5 bg-[#274e13] text-white rounded-sm hover:bg-[#38761d] transition-colors"
            >
              {t.nav.admin}
            </Link>
          ) : (
            <Link
              data-testid="nav-admin-login"
              to="/admin/login"
              className="hidden sm:inline-flex text-xs font-semibold px-3 py-1.5 border border-stone-300 rounded-sm hover:bg-stone-100 transition-colors"
            >
              {t.nav.admin}
            </Link>
          )}

          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden p-2"
            onClick={() => setOpen((o) => !o)}
            aria-label="menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-stone-200 bg-white">
          <div className="px-4 py-4 flex flex-col gap-3">
            <NavLink data-testid="mnav-home" to="/" onClick={() => setOpen(false)} className={linkClass} end>
              {t.nav.home}
            </NavLink>
            <NavLink
              data-testid="mnav-products"
              to="/products"
              onClick={() => setOpen(false)}
              className={linkClass}
            >
              {t.nav.products}
            </NavLink>
            <NavLink
              data-testid="mnav-contact"
              to="/contact"
              onClick={() => setOpen(false)}
              className={linkClass}
            >
              {t.nav.contact}
            </NavLink>
            <button
              data-testid="mnav-lang-toggle"
              onClick={() => {
                toggleLang();
                setOpen(false);
              }}
              className="flex items-center gap-2 text-sm text-stone-700"
            >
              <Languages size={16} /> {lang === "en" ? "తెలుగు" : "English"}
            </button>
            <Link
              data-testid="mnav-admin"
              to={admin ? "/admin" : "/admin/login"}
              onClick={() => setOpen(false)}
              className="text-sm font-semibold text-[#274e13]"
            >
              {t.nav.admin}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
